import React, { useEffect, useMemo, useState } from "react";

const API_URL = "https://cms.amservices.eu/api/isolacier_users_manager.php";
const EMPTY_FORM = { id: null, username: "", password: "", role: "tech", fonction: "" };
const ROLES = {
  tech: { label: "Technicien · son planning uniquement", icon: "🟢" },
  admin: { label: "Administrateur · accès complet", icon: "🔴" },
  superadmin: { label: "Super administrateur · accès complet + utilisateurs", icon: "⭐" },
};

function token() {
  const user = JSON.parse(localStorage.getItem("am_user") || "null");
  return localStorage.getItem("am_token") || user?.token || "";
}

function formatDate(value) {
  if (!value) return "Jamais";
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("fr-FR");
}

export default function IsolacierUsersManager({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const editingAdmin = String(
    users.find(user => Number(user.id) === Number(form.id))?.username || ""
  ).toLowerCase() === "admin";

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter(user =>
      [user.id, user.username, user.role, user.fonction].some(value =>
        String(value || "").toLowerCase().includes(query)
      )
    );
  }, [users, search]);

  const request = async (payload = null) => {
    const authToken = token();
    const response = await fetch(API_URL, {
      method: payload ? "POST" : "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Auth-Token": authToken,
      },
      ...(payload ? { body: JSON.stringify(payload) } : {}),
    });
    const text = await response.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error("Réponse invalide du serveur.");
    }
    if (!response.ok || !data.success) {
      throw new Error(data.message || `Erreur HTTP ${response.status}`);
    }
    return data;
  };

  const loadUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await request();
      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (err) {
      setError(err.message || "Impossible de charger les utilisateurs Isolacier.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async payload => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await request(payload);
      setUsers(Array.isArray(data.users) ? data.users : []);
      setMessage(data.message || "Opération terminée.");
      return true;
    } catch (err) {
      setError(err.message || "L’opération a échoué.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const edit = user => {
    setForm({
      id: Number(user.id),
      username: user.username || "",
      password: "",
      role: ROLES[user.role] ? user.role : "tech",
      fonction: user.fonction || "",
    });
    setError("");
    setMessage("");
  };

  const submit = async event => {
    event.preventDefault();
    const done = await run({
      action: form.id ? "update" : "create",
      id: form.id,
      username: form.username.trim(),
      password: form.password,
      role: form.role,
      fonction: form.fonction.trim(),
    });
    if (done) setForm(EMPTY_FORM);
  };

  const remove = async user => {
    if (!window.confirm(
      `Supprimer ${user.username} du CRM Isolacier, du Kanban et du Planning ?`
    )) return;
    const done = await run({ action: "delete", id: Number(user.id) });
    if (done && Number(form.id) === Number(user.id)) setForm(EMPTY_FORM);
  };

  const syncedCount = users.filter(user =>
    Number(user.synced_planning) && (!['admin', 'superadmin'].includes(user.role) || Number(user.synced_kanban))
  ).length;

  return (
    <main className="users-manager-page users-manager-isolacier">
      <header className="users-manager-header">
        <div className="users-manager-title">
          <span className="um-eyebrow">Administration · Isolacier</span>
          <h1>Utilisateurs Isolacier</h1>
          <p>Les administrateurs ont accès aux outils. Seuls les super administrateurs gèrent aussi les utilisateurs.</p>
        </div>
        <div className="um-current-admin">
          <span>Administrateur CMS connecté</span>
          <strong>{currentUser?.nom || currentUser?.login}</strong>
        </div>
      </header>

      <section className="um-summary" aria-label="Résumé Isolacier">
        <article><strong>{users.length}</strong><span>comptes Isolacier</span></article>
        <article><strong>{syncedCount}</strong><span>comptes synchronisés</span></article>
        <article><strong>{users.filter(user => ['admin', 'superadmin'].includes(user.role)).length}</strong><span>administrateur(s)</span></article>
      </section>

      {message && <div className="um-alert um-alert-success">{message}</div>}
      {error && <div className="um-alert um-alert-error">{error}</div>}

      <div className="um-content-grid">
        <section className="um-panel um-form-panel">
          <div className="um-panel-title">
            <div>
              <span className="um-step">{form.id ? `Compte #${form.id}` : "Nouveau compte"}</span>
              <h2>{form.id ? "Modifier l’utilisateur" : "Ajouter un utilisateur Isolacier"}</h2>
            </div>
            {form.id && (
              <button className="um-link-button" type="button" onClick={() => setForm(EMPTY_FORM)}>
                Annuler
              </button>
            )}
          </div>

          {editingAdmin && (
            <div className="um-master-notice">Le compte super administrateur Isolacier est protégé.</div>
          )}

          <form onSubmit={submit}>
            <div className="um-form-group">
              <label htmlFor="isolacier-username">Identifiant</label>
              <input
                id="isolacier-username"
                className="um-input"
                value={form.username}
                onChange={event => setForm({ ...form, username: event.target.value })}
                disabled={editingAdmin}
                placeholder="Ex. pierre.dupont"
                required
              />
              <span className="um-hint">Le rôle choisi détermine automatiquement les applications autorisées.</span>
            </div>
            <div className="um-form-group">
              <label htmlFor="isolacier-password">
                {form.id ? "Nouveau mot de passe" : "Mot de passe initial"}
              </label>
              <input
                id="isolacier-password"
                className="um-input"
                type="password"
                minLength={8}
                value={form.password}
                onChange={event => setForm({ ...form, password: event.target.value })}
                placeholder={form.id ? "Vide = mot de passe inchangé" : "8 caractères minimum"}
                required={!form.id}
                autoComplete="new-password"
              />
            </div>
            <div className="um-form-group">
              <label htmlFor="isolacier-fonction">Fonction dans l’équipe</label>
              <input
                id="isolacier-fonction"
                className="um-input"
                value={form.fonction}
                onChange={event => setForm({ ...form, fonction: event.target.value })}
                placeholder="Ex. Chef d’équipe, conducteur de travaux"
              />
              <span className="um-hint">Cette personne apparaîtra automatiquement dans le Planning.</span>
            </div>
            <div className="um-form-group">
              <label htmlFor="isolacier-role">Rôle et droits d’accès</label>
              <select
                id="isolacier-role"
                className="um-select"
                value={form.role}
                onChange={event => setForm({ ...form, role: event.target.value })}
                disabled={editingAdmin}
              >
                {Object.entries(ROLES).map(([value, role]) => (
                  <option key={value} value={value}>{role.icon} {role.label}</option>
                ))}
              </select>
            </div>
            <button className="um-btn um-btn-primary" type="submit" disabled={saving}>
              {saving ? "Enregistrement…" : form.id ? "Enregistrer" : "Créer et synchroniser"}
            </button>
          </form>
        </section>

        <section className="um-panel um-list-panel">
          <div className="um-toolbar">
            <div><span className="um-step">Isolacier</span><h2>Équipe enregistrée</h2></div>
            <input
              className="um-input um-search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Rechercher…"
              aria-label="Rechercher un utilisateur Isolacier"
            />
          </div>

          {loading ? <div className="um-loading">Chargement des comptes Isolacier…</div> : (
            <div className="um-table-wrap">
              <table className="um-table">
                <thead>
                  <tr><th>Utilisateur</th><th>Fonction</th><th>Rôle</th><th>Synchronisation</th><th>Dernière connexion</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => {
                    const role = ROLES[user.role] || ROLES.tech;
                    const elevated = ['admin', 'superadmin'].includes(user.role);
                    const synced = Number(user.synced_planning) && (!elevated || Number(user.synced_kanban));
                    const isAdmin = String(user.username).toLowerCase() === "admin";
                    return (
                      <tr key={user.id}>
                        <td>
                          <div className="um-user-cell">
                            <span className="um-avatar isolacier">{String(user.username).charAt(0).toUpperCase()}</span>
                            <span>
                              <strong>@{user.username} {isAdmin && <em className="um-master-badge">Protégé</em>}</strong>
                              <small>Compte #{user.id}</small>
                            </span>
                          </div>
                        </td>
                        <td>{user.fonction || "Non renseignée"}</td>
                        <td><span className={`um-role um-role-${user.role}`}>{role.icon} {role.label}</span></td>
                        <td>
                          <div className="um-sync-status">
                            <span className="is-ok">CRM</span>
                            <span className={Number(user.synced_planning) ? "is-ok" : "is-missing"}>Planning</span>
                            {elevated ? (
                              <span className={Number(user.synced_kanban) ? "is-ok" : "is-missing"}>Kanban</span>
                            ) : (
                              <span className="is-ok">Kanban bloqué</span>
                            )}
                          </div>
                        </td>
                        <td className="um-date">{formatDate(user.last_login)}</td>
                        <td>
                          <div className="um-row-actions">
                            {!synced && (
                              <button className="um-btn um-btn-sync" type="button" onClick={() => run({ action: "sync", id: Number(user.id) })} disabled={saving}>
                                Synchroniser
                              </button>
                            )}
                            <button className="um-btn um-btn-warning" type="button" onClick={() => edit(user)} disabled={saving}>
                              Modifier
                            </button>
                            {!isAdmin && (
                              <button className="um-btn um-btn-danger" type="button" onClick={() => remove(user)} disabled={saving}>
                                Supprimer
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!filteredUsers.length && (
                    <tr><td colSpan="6" className="um-empty">Aucun utilisateur Isolacier trouvé.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
